import type { Discovery } from '#~/device/discovery/index.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import * as Select from '@studio/design-system/select';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { castId } from '@studio/models';
import type { KeyboardEvent, MouseEvent, ReactElement } from 'react';
import type { TransportSelectProps } from './types.js';

/** Transport label as the Protocol column shows it (the Iris transport carries the vendor name). */
const transportLabel = (connection: string): string =>
  connection === 'Iris' ? vendorName() : connection;

// Picker clicks and keys must not also select the row or toggle its checkbox;
// React re-dispatches the portalled menu's events through this wrapper too.
const stopRowHandlers = (event: MouseEvent | KeyboardEvent): void => {
  event.stopPropagation();
};

/**
 * Transport choice on a discovery row for a camera that answered several
 * protocols. Shows the row's current transport; picking another hands the
 * row (and any selection on it) to that transport's discovery.
 *
 * Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — trigger
 * and options carry STags keyed by discovery id for e2e.
 */
export const TransportSelect = ({
  cameraName,
  value,
  transports,
  onChange
}: TransportSelectProps): ReactElement => (
  <div className='flex min-w-0' onClick={stopRowHandlers} onKeyDown={stopRowHandlers}>
    <Select.Root
      onValueChange={(next: string): void => onChange(value, castId<Discovery['id']>(next))}
      size='sm'
      value={value}
    >
      <Select.Trigger
        aria-label={`Connection for ${cameraName}`}
        className='h-24 w-auto min-w-0 max-w-full gap-4 px-8'
        {...STagNew(Sel.AddVideoDeviceDiscovery, 'transport', value)}
      >
        <span className='truncate'>
          <Select.Value />
        </span>
      </Select.Trigger>

      <Select.Content>
        {transports.map((transport) => (
          <Select.Item
            key={transport.id}
            value={transport.id}
            {...STagNew(Sel.AddVideoDeviceDiscovery, 'transport_option', transport.id)}
          >
            {transportLabel(transport.connection)}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  </div>
);
